// Launch gates. Some claims are true only once something happens before the
// site goes live: a future feature ships (F1–F4, the Future Features doc) or
// a pre-launch item is done (C2–C15, docs/refresh/FEATURE_SHOWCASE.md Part C).
// Every gated phrase in src/content carries its gate. Set a gate to false and
// that phrase disappears from every page, with no other edit.
//
// Page gates (L1) hold back whole pages: a route whose `gate` is off isn't
// rendered (App.js), prerendered or in the sitemap (scripts/), and every link
// to it carries the same gate. Keep L1 on one line as `const L1 = false;`:
// craco.config.js reads it, so a production build with it off has no
// comparison code at all (__COMPARE_ON__ in App.js).
const L1 = true; // Comparison pages (/compare). Turned on by the owner, 2026-11-04 (FEATURE_SHOWCASE Part C 18)

// Preview: `npm start` shows page gates' pages and links, so counsel can
// review them before launch. So does a build with
// REACT_APP_PREVIEW_GATES=true (README "Previewing gated pages"); never
// deploy one. Copy gates (F, C) don't preview: they follow GATES as set.
const PREVIEW = process.env.NODE_ENV === 'development' || process.env.REACT_APP_PREVIEW_GATES === 'true';
const PAGE_GATES = ['L1'];

export const GATES = {
  L1,
  F1: true, // White-label rooms
  F2: true, // AI-drafted Q&A answers
  F3: true, // Q&A answer approval
  F4: true, // Data isolation on request
  C2: true, // SSO, session revocation and deal pinning switched on in production
  C3: true, // Earnings-quality and concentration signals switched on
  C4: true, // Two-step sign-in requirement enforced
  C5: true, // Hardened deal entry available
  C8: true, // AI provider terms confirmed in writing (no training on documents)
  C10: true, // Lead replies within one business day
  C11: true, // Security documentation pack exists
  C12: true, // Security contact monitored
  C13: true, // "Trusted by" customers can be referenced
  C14: true, // Lead-form privacy line matches the Privacy Policy (reworded 2026-10-22)
  C15: true, // support@ and privacy@ mailboxes exist and are watched (off: both fall back to info@)
  // Not a launch item: on exactly when the build sets REACT_APP_GA_MEASUREMENT_ID,
  // the same value that loads Google Analytics (public/index.html), so the
  // Privacy Policy names it only while the site runs it.
  GA: /^G-[A-Z0-9]+$/.test(process.env.REACT_APP_GA_MEASUREMENT_ID || ''),
};

export const isOn = (gate) => !gate || GATES[gate] !== false || (PREVIEW && PAGE_GATES.includes(gate));

// A list entry is a string or { text, gate } (or any object with a gate).
export const gated = (items) => items.filter((item) => typeof item === 'string' || isOn(item.gate));

// A paragraph is one string, or an array of sentences that may carry gates.
export const sentences = (body) =>
  Array.isArray(body)
    ? gated(body)
        .map((part) => (typeof part === 'string' ? part : part.text))
        .join(' ')
    : body;

// Copy as plain text, for structured data and meta tags: [label](href) links
// become their label and **bold** marks are dropped.
export const plain = (body) =>
  sentences(body)
    .replace(/\[([^\]]+)\]\([^)\s]+\)/g, '$1')
    .replace(/\*\*/g, '');
